// HTTP client now lives in api/client.js. This module keeps the auth/org API
// helpers and re-exports the shared client so existing imports keep working.
import api, { aiApi } from './client';

export { aiApi };

export const authAPI = {
  // Register new user
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  // Classic password login (unchanged for Phase 1-3; deleted in Phase 4).
  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },

  // ─── Email-OTP auth (Phase 2 of EMAIL_OTP_AUTH_PRD) ────────────────
  // Non-enumerating account probe. Returns { hasPassword, canUseOtp }.
  lookup: async (email) => {
    const response = await api.post('/auth/lookup', { email });
    return response.data;
  },

  // Request an OTP be emailed. `purpose` = 'login' | 'password_reset'.
  // In preview (PREVIEW_OTP_ECHO=true) the response contains `_devCode`
  // for local dev; in production only the email carries the code.
  requestOtp: async (email, purpose = 'login') => {
    const response = await api.post('/auth/otp/request', { email, purpose });
    return response.data;
  },

  // Verify OTP + get access token (same shape as classic /login).
  verifyOtp: async ({ email, code, orgId }) => {
    const payload = { email, code };
    if (orgId) payload.orgId = orgId;
    const response = await api.post('/auth/otp/verify', payload);
    return response.data;
  },

  // Reset password via OTP (replaces the reset-token email link flow).
  resetPasswordViaOtp: async ({ email, code, newPassword }) => {
    const response = await api.post('/auth/password/reset-via-otp', {
      email, code, newPassword,
    });
    return response.data;
  },

  // Post-OTP nudge — orphan users set a password after their first login.
  setPassword: async (newPassword) => {
    const response = await api.post('/auth/password/set', { newPassword });
    return response.data;
  },

  // Post-OTP nudge — user chose to keep using OTP; suppress the modal.
  skipPasswordNudge: async () => {
    const response = await api.post('/auth/password/skip-nudge');
    return response.data;
  },

  // Self-onboard (create org and admin user)
  selfOnboard: async (orgData, adminData) => {
    // Backend expects flat structure with all fields
    const payload = {
      // Organization fields
      name: orgData.name,
      type: orgData.type,
      logoUrl: orgData.logoUrl || null,
      orgDomain: orgData.orgDomain || null,
      // Admin user fields
      email: adminData.email,
      password: adminData.password,
      firstName: adminData.firstName,
      lastName: adminData.lastName,
      orgId: adminData.orgId,
      role: 'TenantAdmin'
    };
    const response = await api.post('/auth/self-onboard', payload);
    return response.data;
  },

  // Get current user
  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

export const organizationAPI = {
  // Super Admin: Create organization
  createOrganization: async (orgData) => {
    const response = await api.post('/superadmin/organizations', orgData);
    return response.data;
  },

  // Super Admin: List all organizations
  listOrganizations: async () => {
    const response = await api.get('/superadmin/organizations');
    return response.data;
  },

  // Get organization details
  getOrganization: async (orgId) => {
    const response = await api.get(`/organizations/${orgId}`);
    return response.data;
  },

  // Update organization
  updateOrganization: async (orgId, updateData) => {
    const response = await api.put(`/organizations/${orgId}`, updateData);
    return response.data;
  },

  // Update SSO configuration
  updateSSO: async (orgId, ssoConfig) => {
    const response = await api.put(`/organizations/${orgId}/sso`, ssoConfig);
    return response.data;
  },

  // List organization users (TenantAdmin+ only - full user details)
  listUsers: async (orgId) => {
    const response = await api.get(`/organizations/${orgId}/users`);
    return response.data;
  },
  
  // List basic user info (Instructor+ - for display purposes)
  listUsersBasic: async (orgId, role = null) => {
    const params = role ? `?role=${role}` : '';
    const response = await api.get(`/organizations/${orgId}/users/basic${params}`);
    return response.data;
  },

  // Create user in organization
  createUser: async (orgId, userData) => {
    const response = await api.post(`/organizations/${orgId}/users`, userData);
    return response.data;
  },

  // Get tags
  getTags: async (orgId) => {
    const response = await api.get(`/organizations/${orgId}/tags`);
    return response.data;
  },

  // Get cohorts
  getCohorts: async (orgId) => {
    const response = await api.get(`/organizations/${orgId}/cohorts`);
    return response.data;
  },
};

export default api;
